/**
 * ProgramRecord — the per-program obligation ledger (RFC-0004 Phase 1d TRACK).
 *
 * This is the first *system of record* in regpulse-core: the deterministic
 * evaluator stays pure (ARCHITECTURE invariant 2), and a ProgramRecord is its
 * persistent projection — which obligations a specific vehicle program is
 * tracking and where each one stands (open → actioned → closed). Phase 1 is
 * SINGLE-TENANT (one program); multi-tenant is deferred to Phase 4 (WEDGE.md:69).
 *
 * Red line (ADR-0002 §8 / KAIYANG §8): this ledger records obligation STATUS and
 * the reviewer who signed it — it never assigns work. There is deliberately no
 * owner / assignee / dispatch field. RegPulse tracks "open/closed + who signed",
 * it does not run a workflow.
 */

import { z } from "zod";

export const ObligationItemStatus = z.enum([
  "open", // surfaced (e.g. by a source change), not yet acted on
  "actioned", // work is under way / evidence being gathered
  "closed", // reviewer signed it off
  "dismissed", // reviewer judged it not applicable to this program
]);
export type ObligationItemStatus = z.infer<typeof ObligationItemStatus>;

export const ObligationItemSchema = z.object({
  obligationItemId: z.string().min(1),
  ruleId: z.string().min(1),
  status: ObligationItemStatus,
  // The source-change / discovery signal that opened this item (null if seeded
  // from the initial config→applicability pass rather than a change).
  openedBySignalId: z.string().nullable(),
  openedAt: z.string().min(1),
  updatedAt: z.string().min(1),
  // Free-text reviewer note — NEVER an owner/assignee (ADR-0002 §8).
  note: z.string().nullable(),
});
export type ObligationItem = z.infer<typeof ObligationItemSchema>;

export const ProgramRecordSchema = z.object({
  programId: z.string().min(1),
  programCode: z.string().min(1), // human label, e.g. "omoda5-eu27-my2027"
  // Reference + provenance of the MarketEntryConfig this program tracks.
  configRef: z.string().min(1),
  configSnapshotSha256: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  obligations: z.array(ObligationItemSchema),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});
export type ProgramRecord = z.infer<typeof ProgramRecordSchema>;
